import _colorsDict from "../../dict/colors.js";
import _getNearestColor from "../utility/getNearestColor.js";
import _escapeTextContent from "../quill/escapeTextContent.js";
import _cleanExtraSpanTags from "../quill/cleanExtraSpanTags.js";
import _combineSpanTags from "../quill/combineSpanTags.js";
import _flexbox from "../../dict/flexbox.js";
import _convertToPlainText from "../quill/convertToPlainText.js";
import _stripTags from "../quill/stripTags.js";
import _getLongestLine from "../utility/getLongestLine.js";
import { decode as _decodeHtml } from "html-entities";
import _eol from "eol";
import _findCenter from "../utility/findCenter.js";
import _quillClasses from "../../dict/quillClasses.js";
import _convertToHtml from "../quill/convertToHtml.js";
import _tcLib from "../../lib/timecode.js";
import _Event from "../../classes/event.js";
/* Creating a single TTML functions file for managing all of the different functions we use across profiles. */

/* Multi-line profiles: */
/* 
    Ttml
*/

/* Single-line profiles: */
/* 
    NetflixTtCaptions
*/

const Event = _Event;
const tcLib = _tcLib;
const convertToHtml = _convertToHtml;
const quillClasses = _quillClasses;
const findCenter = _findCenter;
const eol = _eol;
const decodeHtml = _decodeHtml;
const getLongestLine = _getLongestLine;
const stripTags = _stripTags;
const convertToPlainText = _convertToPlainText;
const flexbox = _flexbox;
const combineSpanTags = _combineSpanTags;
const cleanExtraSpanTags = _cleanExtraSpanTags;
const escapeTextContent = _escapeTextContent;
const getNearestColor = _getNearestColor;
const colorsDict = _colorsDict;
const {
  toWebColor
} = colorsDict;
const ttmlGeneral = {
  /* General Functions */
  frameRateMap: {
    "23.976": "24",
    "24": "24",
    "25": "25",
    "29.97": "30",
    "30": "30",
    "50": "50",
    "59.94": "60",
    "60": "60"
  },
  frameRateMultiplierMap: {
    "23.976": "1000 1001",
    "24": "1 1",
    "25": "1 1",
    "29.97": "1000 1001",
    "30": "1 1",
    "50": "1 1",
    "59.94": "1000 1001",
    "60": "1 1"
  },
  eventStyleMap: {
    "Pop-On": "pop",
    "Roll-Up 2": "rollup",
    "Roll-Up 3": "rollup",
    "Roll-Up 4": "rollup",
    "Paint-On": "paint"
  },
  getStyleFromRegionId: function (regionId) {
    const styleByRegionId = {
      "paint": "Paint-On",
      "paint2": "Paint-On",
      "paint3": "Paint-On",
      "paint4": "Paint-On",
      "rollup2": "Roll-Up 2",
      "rollup3": "Roll-Up 3",
      "rollup4": "Roll-Up 4"
    };
    if (!regionId) {
      return "Pop-On";
    }
    return styleByRegionId[regionId.toString().toLowerCase()] || "Pop-On";
  },
  getTimeBaseValue: function (ttmlString) {
    let regex = /ttp:timeBase="([^"]+)"/;
    let match = ttmlString.match(regex);
    return match ? match[1] : null;
  },
  /* IMSC forced display (https://www.w3.org/TR/ttml-imsc1.1/#itts-forcedDisplay). Omitted when not forced (initial value is false). */
  forcedDisplayAttr: function (event) {
    return event.forced === true ? ` itts:forcedDisplay="true"` : "";
  },
  singleLine: {
    Display: class {
      constructor(options = {
        style: "Pop-On",
        start: false,
        end: false,
        lines: []
      }) {
        this.style = options.style || "Pop-On", this.start = options.start || false, this.end = options.end || false, this.lines = options.lines || [];
      }
      insertLine(options = {
        text: "",
        html: "",
        original: "",
        extentX: 0,
        extentY: 0,
        originX: 0,
        originY: 0,
        region: {},
        textAlign: undefined
      }) {
        this.lines.push({
          text: options.text || "",
          html: options.html || "",
          original: options.original || "",
          extentX: options.extentX || 0,
          extentY: options.extentY || 0,
          originX: options.originX || 0,
          originY: options.originY || 0,
          region: options.region || {},
          textAlign: options.textAlign
        });
      }
    },
    decodeDisplay: function (display, win) {
      let ccEvent = new Event();
      let html = display.lines.map(line => {
        return line.html;
      }).join("\n");
      ccEvent.start = display.start;
      ccEvent.end = display.end;
      let positionInfo = this.getPositionInfo(display, win);
      ccEvent.alignment = positionInfo.alignment;
      ccEvent.xPos = positionInfo.xPos;
      ccEvent.yPos = positionInfo.yPos;
      ccEvent.text = convertToHtml(html, [quillClasses.align[positionInfo.alignment]]);
      //console.log(ccEvent.text);
      return ccEvent;
    },
    getPositionInfo: function (display, win) {
      let xPos,
        yPos,
        alignment = "center",
        details;
      let lines = display.lines || [];
      let line01 = lines[0] || {};

      /* Prefer explicit textAlign from style/paragraph/region when present */
      let explicitAlign = lines.find(line => line.textAlign)?.textAlign;
      if (explicitAlign && flexbox.alignmentNormalize[explicitAlign]) {
        alignment = flexbox.alignmentNormalize[explicitAlign];
      } else {
        details = this.getPosDetails(lines);
        alignment = this.getDisplayAlignment(details);
      }
      let origins = {
        x: parseFloat(line01.originX) || 0,
        y: parseFloat(line01.originY) || 0
      };
      let extents = {
        x: parseFloat(line01.extentX) || 0,
        y: parseFloat(line01.extentY) || 0
      };
      let displayAlign = line01.region && line01.region["@_tts:displayAlign"];
      let totalLines = lines.reduce((count, line) => {
        let source = (line.html || line.text || line.original || "").toString();
        return count + Math.max(1, source.split(/\n|<br\s*\/?>/i).length);
      }, 0) || 1;
      let x = this.getXPos(lines, details, alignment, win);
      let y = this.getYPos(lines, win);

      // Prefer shared multiLine geometry when origin/extent are available
      if (extents.x || extents.y || origins.x || origins.y) {
        x = ttmlGeneral.multiLine.getXPos(origins, extents, win, alignment);
        y = ttmlGeneral.multiLine.getYPos(origins, extents, displayAlign, win, totalLines);
      }
      xPos = x.position;
      yPos = y.position;
      return {
        alignment: alignment,
        xPos: xPos,
        yPos: yPos
      };
    },
    getPosDetails: function (lines) {
      return lines.map(line => {
        let alignment = "right";
        let center = findCenter(100, line.extentX);
        if (line.originX > center - 5 && line.originX < center + 5) {
          alignment = "center";
        } else if (line.originX < center) {
          alignment = "left";
        }
        return {
          start: line.originX,
          end: parseFloat(line.originX) + parseFloat(line.extentX),
          length: line.text.length,
          alignment: alignment
        };
      });
    },
    getDisplayAlignment: function (details) {
      if (details.length === 1) {
        return details[0].alignment;
      }
      let xPosSame = details.every(detail => {
        return detail.start === details[0].start;
      });
      let endSame = details.every(detail => {
        return detail.end === details[0].end;
      });
      let alignmentCenter = details.every(detail => {
        return detail.alignment === "center";
      });
      if (alignmentCenter) {
        return "center";
      } else if (xPosSame) {
        return "left";
      } else if (endSame) {
        return "right";
      } else {
        return "center";
      }
    },
    getXPos: function (lines, details, alignment, win) {
      if (lines && lines.length > 0 && win && win.width) {
        let line01 = lines[0];
        let origins = {
          x: parseFloat(line01.originX) || 0,
          y: parseFloat(line01.originY) || 0
        };
        let extents = {
          x: parseFloat(line01.extentX) || 0,
          y: parseFloat(line01.extentY) || 0
        };
        if (extents.x || extents.y || origins.x || origins.y) {
          return ttmlGeneral.multiLine.getXPos(origins, extents, win, alignment);
        }
      }
      let xInfo = {
        position: "center"
      };
      if (!lines || lines.length === 0 || !win || !win.width) {
        return xInfo;
      }
      if (alignment === "left") {
        xInfo.position = "start";
      } else if (alignment === "center") {
        xInfo.position = "center";
      } else {
        xInfo.position = "end";
      }
      return xInfo;
    },
    getYPos: function (lines, win) {
      if (lines && lines.length > 0 && win && win.height) {
        let line01 = lines[0];
        let origins = {
          x: parseFloat(line01.originX) || 0,
          y: parseFloat(line01.originY) || 0
        };
        let extents = {
          x: parseFloat(line01.extentX) || 0,
          y: parseFloat(line01.extentY) || 0
        };
        let displayAlign = line01.region && line01.region["@_tts:displayAlign"];
        let totalLines = lines.reduce((count, line) => {
          let source = (line.html || line.text || line.original || "").toString();
          return count + Math.max(1, source.split(/\n|<br\s*\/?>/i).length);
        }, 0) || 1;
        if (extents.x || extents.y || origins.x || origins.y) {
          return ttmlGeneral.multiLine.getYPos(origins, extents, displayAlign, win, totalLines);
        }
      }
      let yInfo = {
        position: "end"
      };
      let line01 = lines[0];
      let numberOfLines = lines.length;
      let lineHeight = parseFloat(line01.extentY);
      let linesPerWindow = (100 - win.yPadding * 2) / lineHeight; // How many lines can we fit on the screen based on the extent (size) of line 1. e.g. 80/5.33 = ~15 lines

      //Calculate the line number if 1 is the top of the screen.
      let lineNumberFromTop = Math.round((line01.originY - win.yPadding) / lineHeight) + 1;
      if (line01.region["@_tts:displayAlign"] === "after") {
        //If the displayAlign is 'after' then we need to adjust the line number from top to be based on the bottom of the extent area. For example, if the extentY size is 80%, then we need to say that the line number from top is actually the originY + extentY. This will give us the line number based on the bottom of the text area.

        lineNumberFromTop = win.height * ((line01.originY + lineHeight) / 100);
      }
      if (lineNumberFromTop <= 1) {
        yInfo.position = "start";
      } else if (lineNumberFromTop + numberOfLines > linesPerWindow) {
        yInfo.position = "end";
      } else {
        //Limit to line number between 1 and win.height
        yInfo.position = Math.min(win.height, Math.max(1, lineNumberFromTop));
      }
      return yInfo;
    },
    calcExtents: function (text, win, fontSize = 80) {
      /* 80 is the font size (80%). 5.33 is the font height (5.33%) (1/19))*/
      let totalChars = win?.width ?? 32;
      let xExtent = stripTags(text).trim().length / totalChars * fontSize;
      let yExtent = "5.33";
      return `${xExtent.toFixed(2)}% ${yExtent}%`;
      /*e.g 37.5% 5.33% */
    },
    calcOrigin: function (event, text, lineNumber, totalLines, fontSize = 80, win) {
      let xOrigin = this.calcXOrigin(event, text, fontSize, win);
      let yOrigin = this.calcYOrigin(event, lineNumber, totalLines, fontSize, win);
      return `${xOrigin.toFixed(2)}% ${yOrigin.toFixed(2)}%`; /*e.g 37.5% 5.33% */
    },
    calcXOrigin(event, text, fontSize = 80, win) {
      /* 
          To calculate the x origin % of a single caption line we need to consider the window properies (xPadding and width). xPadding is measured in % of the total screen width, and the width is measured in characters. We then need to know the event position which can be a flexbox value (start, center, end) or a character offset (1-width). Finally we need to know the alignment of the text (left, center, right). We also need to know the length of the longest line in the Event, and if the line we're positioning is the longest line.
            From here we can break things down into 4 scenarios: event.xPos is set to start, center, end, or a character position (1-width). Inside of each of those scenarios we then need to consider the alignment of the text (left, center, right). If the event.xPos is set to a character position (1-width) then we can treat the event xPos as if it were "start" and calculate the offset based on the character offset and window width.
            When xPos is set to "start":
              If the line is longest line then we also return the minXOrigin.
              WHEN THE LINE IS NOT THE LONGEST LINE:
                  If alignment is left: xOrigin = minXOrigin (window xPadding)
                  If the alignment is center we need to find the xOrigin based on the longest line. The longest line would be set at the minXOrigin, and all other lines would be set to the right of that based on the difference in length between the longest line and the current line. For example, if the extents of the longest line is 20% and the extents of the current line is 15% we would need to offset the xOrigin of the line by 2.5% (minXOrigin + (20-15)/2).
                  If the alignment is right we need to do the same thing as when the alignment is center, however we don't need to divide the extent by 2 (minXOrigin + (20-15)). This will align all lines to the right.
          When xPos is set to "center":
              If the line is the longest line then we need to just find the xOrigin based on finding the center (xOrigin = findCenter(100, xExtent)).
              WHEN THE LINE IS NOT THE LONGEST LINE:
                  If the alignment is left we then just use the xOrigin of the longest line: xOrigin = findCenter(100, xExtentOfLongestLine)
                  If the alignment is center we then just need to find the xOrigina based on finding the center (same as if it were longest line): (xOrigin = findCenter(100, xExtent)).
                  If the alignment is right we then need to find the xOrigin of the longest line and calculate the offset: xOrigin = findCenter(100, xExtentOfLongestLine) + (findCenter(100, xExtent)- findCenter(100, xExtentOfLongestLine).
          When the xPos is set to "end":
              We need to take the maxXOrigin and subtract the extent of the current line.          
          When the xPos is set to a character offset:
              We need to treat it the same as if the xPos was set to 'start' but then we need to convert the character offset to a percent and add it to the xOrigin. For example: characterOffset = (event.xPos/win.width)*100;      
      */

      let totalChars = win?.width ?? 32;
      let plainText = convertToPlainText(event.text);
      let longestLine = getLongestLine(plainText) || "";
      let textLength = stripTags(text).length;
      let xExtent = textLength / totalChars * fontSize;
      let xExtentOfLongestLine = longestLine.length / totalChars * fontSize;
      let minXOrigin = win?.xPadding ?? 10;
      let maxXOrigin = 100 - (win?.xPadding ?? 10);
      let alignment = (event.alignment || "center").toLowerCase();
      let isLongestLine = textLength === longestLine.length;
      let extentDifference = Math.max(0, xExtentOfLongestLine - xExtent);
      let xOrigin;
      let clampToWindow = value => Math.min(maxXOrigin, Math.max(minXOrigin, value));
      let getStartOrigin = () => {
        if (isLongestLine || alignment === "left") {
          return minXOrigin;
        }
        if (alignment === "center") {
          return minXOrigin + extentDifference / 2;
        }
        return minXOrigin + extentDifference;
      };
      if (event.xPos === "start" || typeof event.xPos === "number" && event.xPos <= 1) {
        xOrigin = getStartOrigin();
      } else if (event.xPos === "center") {
        let centerOrigin = findCenter(100, xExtent);
        let centerOriginLongest = findCenter(100, xExtentOfLongestLine);
        if (isLongestLine || alignment === "center") {
          xOrigin = centerOrigin;
        } else if (alignment === "left") {
          xOrigin = centerOriginLongest;
        } else {
          xOrigin = centerOriginLongest + extentDifference;
        }
      } else if (event.xPos === "end") {
        xOrigin = 100 - (xExtent + Math.round(win.xPadding / 2));
      } else {
        let charOffset = parseInt(event.xPos, 10);
        let offsetPercent = !isNaN(charOffset) && totalChars ? charOffset / totalChars * 100 : 0;
        xOrigin = getStartOrigin() + offsetPercent;
      }
      return parseFloat(clampToWindow(xOrigin));
    },
    calcYOrigin(event, lineNumber, totalLines, fontSize = 80, win) {
      /* 
          To calculate the y origin % of a single caption line we need to consider the window properies (yPadding and height). yPadding is measured in % of the total screen height, and the height is measured in lines. We then need to know the event position which can be a flexbox value (start, center, end) or a line offset (1-height). Finally we need to know the line number of the current line (0-based) and the total number of lines in the event.
          
          To start we need to calculate the line height based on the window height and yPadding. For example, if the window height is 15, and the window yPadding is 10% we need to calculate the padding in lines for both top and bottom ((15*0.1)*2 = 18). Now we know that each line will occupy 1/18 (5.55%). This can be used to calculate our vertical offset based on the lineNunber (lineNumber*lineHeight = offset). If we're placing the second line the offset would be 1*5.55 = 5.55. We will need to add this onto the yOrigin we're calculating.
          
          There are 4 scenarios we need to be walk through to calculate the yOrigin. When yPos is start, center, end, or a line offset.
            If the yPos value is "start", we can then set the yOrigin to the window yPadding and add the line Offset.
            If the yPos value is set to "end" we need to calculate the maximum yOrigin value (100-window.yPadding), then subtract the line offset (lineHeight*(totalLines-lineNumber)). For example, if we're placing the first of 2 lines at the bottom of the screen we would calculate the max yOrigin by going 100-10 = 90. The line offset would be 5.55*(2-0)= 11.1. So the yOrigin would be 90-11.1 = 78.9. The second line would be 90-5.55 = 84.45.
            If the yPos value is set to "center" we need to find the center of the screen (50%) and then subtract half of the total lines height to find the starting yOrigin. For example, if we have 2 lines and each line is 5.55 high, the total height would be 11.1. Half of that is 5.55 so we would start at 50-5.55 = 44.45. From there we would add the line offset (lineNumber*lineHeight). So the first line would be 44.45+0=44.45 and the second line would be 44.45+5.55=50.
            If the yPos value is set to a line offset (1-height) we need to treat it the same as if it were "start" but then we need to convert the line offset to a percent and add it to the yOrigin. In this case we have two line offsets - the first is the line offset from yPos, and the second is line offset from the line index. For example: EventLineOffset = (event.yPos/win.height)*100; LineIndexOffset = lineNumber*lineHeight; yOrigin = window.yPadding + EventLineOffset + LineIndexOffset;
      */

      let yPadding = Math.max(0, win?.yPadding ?? 10);
      let lineHeight = 5.33;
      let totalLineCount = Math.max(1, totalLines ?? 1);
      let currentLine = Math.max(0, lineNumber ?? 0);
      let lineOffset = currentLine * lineHeight;
      let clampToWindow = value => {
        let minOrigin = yPadding;
        let maxOrigin = 100 - yPadding;
        if (!isFinite(value)) {
          return minOrigin;
        }
        return Math.min(maxOrigin, Math.max(minOrigin, value));
      };
      let getNumericOffset = value => {
        if (value === undefined || value === null) {
          return null;
        }
        if (typeof value === "number" && !isNaN(value)) {
          return value;
        }
        let parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };
      let rawYPos = event?.yPos;
      let yPos = typeof rawYPos === "string" ? rawYPos.toLowerCase() : rawYPos;
      let yOrigin;
      if (yPos === "start") {
        yOrigin = yPadding + lineOffset;
      } else if (yPos === "center") {
        let blockHeight = totalLineCount * lineHeight;
        let startAtCenter = findCenter(100, blockHeight);
        yOrigin = startAtCenter + lineOffset;
      } else if (yPos === "end") {
        let maxOrigin = 100 - yPadding;
        let remainingLines = totalLineCount - currentLine;
        yOrigin = maxOrigin - remainingLines * lineHeight;
      } else if (getNumericOffset(yPos) !== null) {
        let eventLineOffset = getNumericOffset(yPos) * lineHeight - 5.33;
        yOrigin = yPadding + eventLineOffset + lineOffset;
      } else {
        yOrigin = yPadding + lineOffset;
      }
      return parseFloat(clampToWindow(yOrigin));
    },
    convertToTtml: function (html) {
      // console.log(html);
      html = html.replace(/(<strong><em><u>)+/gmi, "<span tts:fontWeight='bold' tts:fontStyle='italic'  tts:textDecoration='underline'>").replace(/(<\/u><\/em><\/strong>)+/gmi, "</span>").replace(/(<strong><u>)+/gmi, "<span tts:fontWeight='bold' tts:textDecoration='underline'>").replace(/(<\/u><\/strong>)+/gmi, "</span>").replace(/(<strong><em>)+/gmi, "<span tts:fontWeight='bold' tts:fontStyle='italic'>").replace(/(<\/em><\/strong>)+/gmi, "</span>").replace(/(<em><strong>)+/gmi, "<span tts:fontWeight='bold' tts:fontStyle='italic'>").replace(/(<\/strong><\/em>)+/gmi, "</span>").replace(/(<strong>)+/gmi, "<span tts:fontWeight='bold'>").replace(/(<\/strong>)+/gmi, "</span>").replace(/(<em><u>)+/gmi, "<span tts:fontStyle='italic'  tts:textDecoration='underline'>").replace(/(<\/u><\/em>)+/gmi, "</span>").replace(/(<em>)+/gmi, "<span tts:fontStyle='italic'>").replace(/(<\/em>)+/gmi, "</span>").replace(/(<u>)+/gmi, "<span tts:textDecoration='underline'>").replace(/(<\/u>)+/gmi, "</span>").replace(/<span\s+style=['"]color:\s*([^;'"]+)[;]?['"]/gi, "<span tts:color='$1'");
      html = combineSpanTags(html);
      html = cleanExtraSpanTags(html);

      // console.log("AFTER:", html);
      html = html.replace(/(<\/span>)+/g, "</span>").replace(/(?:\r\n|\r|\n)/g, "<br/>");
      return escapeTextContent(html);
    }
  },
  multiLine: {
    /* Decode Functions */
    decodeSubtitle: function (subtitle, region, style, frameRate, dropFrame, useClockTime, win, ignorePos = false) {
      let ccEvent = new Event();
      let start = tcLib.parseTcToSec(subtitle["@_begin"], frameRate, dropFrame, useClockTime);
      let end = tcLib.parseTcToSec(subtitle["@_end"], frameRate, dropFrame, useClockTime);
      // console.log("--------------------------------");
      // console.log("Before:",subtitle["#text"])
      let html = this.formatText(subtitle["#text"]);
      // console.log(html);
      if (!ignorePos) {
        let positionInfo = this.getPositionInfo(subtitle, region, style, win);
        // console.log(positionInfo);
        ccEvent.alignment = positionInfo.alignment;
        ccEvent.xPos = positionInfo.xPos;
        ccEvent.yPos = positionInfo.yPos;
        ccEvent.text = convertToHtml(html, [quillClasses.align[positionInfo.alignment]]);
      } else {
        ccEvent.text = convertToHtml(html);
      }
      ccEvent.start = start;
      ccEvent.end = end;
      ccEvent.forced = subtitle["@_itts:forcedDisplay"] === "true";
      return ccEvent;
    },
    removeRedundantTags: function (input) {
      let tags = ['em', 'u']; // Add more tags here if needed
      let result = input;
      for (let tag of tags) {
        let regex = new RegExp(`<${tag}>((?:<${tag}>.*?<\/${tag}>|.)*?)<\/${tag}>`, 'g');
        let previousResult;
        do {
          previousResult = result;
          result = result.replace(regex, (match, content) => {
            // Remove inner tags of the same type
            content = content.replace(new RegExp(`<${tag}>|<\/${tag}>`, 'g'), '');
            return `<${tag}>${content}</${tag}>`;
          });
        } while (result !== previousResult);
      }
      return result;
    },
    replaceNestedItalicSpans: function (input) {
      let regex = /<span tts:fontStyle="italic">((?:[^<]|<(?!span tts:fontStyle="italic"))*(?:<span tts:fontStyle="italic">(?:[^<]|<(?!span tts:fontStyle="italic"))*<\/span>)*(?:[^<]|<(?!span tts:fontStyle="italic"))*)<\/span>/g;
      let result = input;
      let previousResult;
      do {
        previousResult = result;
        result = result.replace(regex, '<em>$1</em>');
      } while (result !== previousResult);
      return result;
    },
    transformTTMLColors: function (input) {
      if (!input || typeof input !== 'string') {
        throw new Error('Input must be a non-empty string');
      }
      let ttmlColorRegex = /<span\s+((?:[^>]*\s+)?)?tts:color\s*=\s*(['"])([^'"]+)\2(\s+[^>]*)?>[\s\n]*([\s\S]*?)[\s\n]*<\/span>/g;
      let transformed = input.replace(ttmlColorRegex, (match, preAttrs, quote, color, postAttrs, content) => {
        if (!color) {
          console.warn('Empty color value found in TTML metadata');
          return match;
        }
        let otherAttrs = [preAttrs, postAttrs].filter(Boolean).join(' ').replace(/\s+tts:color\s*=\s*(['"])[^'"]*\1\s*/g, ' ').trim();
        try {
          let nearestColor = getNearestColor(color);
          if (nearestColor) {
            color = nearestColor.name;
          }
        } catch (err) {
          console.log(err.message);
        }
        return `<span${otherAttrs ? ' ' + otherAttrs : ''}><${color}>${content}</${color}></span>`;
      });
      return transformed;
    },
    transformTTMLBackgroundColors: function (input) {
      if (!input || typeof input !== 'string') {
        throw new Error('Input must be a non-empty string');
      }
      let ttmlBgColorRegex = /<span\s+((?:[^>]*\s+)?)?tts:backgroundColor\s*=\s*(['"])([^'"]+)\2(\s+[^>]*)?>[\s\n]*([\s\S]*?)[\s\n]*<\/span>/g;
      let transformed = input.replace(ttmlBgColorRegex, (match, preAttrs, quote, color, postAttrs, content) => {
        if (!color || color === "transparent") {
          // console.warn('Empty background color value found in TTML metadata');
          return match;
        }
        let otherAttrs = [preAttrs, postAttrs].filter(Boolean).join(' ').replace(/\s+tts:backgroundColor\s*=\s*(['"])[^'"]*\1\s*/g, ' ').trim();
        try {
          let nearestColor = getNearestColor(color);
          if (nearestColor) {
            color = nearestColor.name;
          }
        } catch (err) {
          console.log("Error transforming background color:", err.message);
        }
        let bgTag = `bg${color.charAt(0).toUpperCase() + color.slice(1)}`;
        return `<span${otherAttrs ? ' ' + otherAttrs : ''}><${bgTag}>${content}</${bgTag}></span>`;
      });
      return transformed;
    },
    replaceSpanTagsWithFormatTags: function (str) {
      //Look after just the italic tags
      if (!str) {
        return "";
      }
      str = this.replaceNestedItalicSpans(str);
      str = this.transformTTMLBackgroundColors(str);
      str = this.transformTTMLColors(str);

      // Replace <span> tags with italic and underline attributes
      //console.log(str);
      str = str.replace(/<span\s+tts:fontStyle\s*=\s*(['"]?)italic\1\s+tts:textDecoration\s*=\s*(['"]?)underline\2\s*>(.*?)<\/span>/gmi, '<em><u>$3</u></em>');

      // Replace <span> tags with italic attribute
      str = str.replace(/<span\s+tts:fontStyle\s*=\s*(['"]?)italic\1\s*>(.*?)<\/span>/gmi, '<em>$2</em>');

      // Replace <span> tags with italic attribute
      str = str.replace(/<span\s+style\s*=\s*(['"]?)italic\1\s*>(.*?)<\/span>/gi, '<em>$2</em>');

      // Replace <span> tags with underline attribute
      str = str.replace(/<span\s+tts:textDecoration\s*=\s*(['"]?)underline\1\s*>(.*?)<\/span>/gmi, '<u>$2</u>');

      // Replace <span> tags with italic attribute
      str = str.replace(/<span[^>]*style\s*=\s*["'][^"']*["'][^>]*>(.*?)<\/span>/gmi, '<em>$1</em>');
      str = str.replace(/<span[^>]*>(.*?)<\/span>/gmi, '$1');
      str = str.replace(/<\/?span[^>]*>/gmi, "");
      //console.log(str);

      str = this.removeRedundantTags(str);
      return str;
    },
    formatText: function (text) {
      if (!text) {
        return "";
      }
      text = text.toString();

      // Handle spans that contain break tags by preserving span attributes across breaks
      text = this.preserveSpansAcrossBreaks(text);

      // Now split by breaks and process each line
      let segments = text.split(/<br\/>|<br>|<br \/>/).map(segment => {
        return this.replaceSpanTagsWithFormatTags(segment).trim();
      });
      let fText = segments.join("\n");
      return decodeHtml(fText.trim());
    },
    preserveSpansAcrossBreaks: function (text) {
      // Find span tags that contain break tags and split them appropriately
      let spanWithBreakRegex = /<span([^>]*)>((?:[^<]|<(?!\/span>|br\/?>))*?)<br\s*\/?>((?:[^<]|<(?!\/span>))*?)<\/span>/gi;
      let result = text;
      let match;

      // Keep processing until no more spans with breaks are found
      while ((match = spanWithBreakRegex.exec(result)) !== null) {
        let fullMatch = match[0];
        let attributes = match[1];
        let beforeBreak = match[2];
        let afterBreak = match[3];

        // Create separate spans for content before and after the break
        let replacement = `<span${attributes}>${beforeBreak}</span><br/><span${attributes}>${afterBreak}</span>`;
        result = result.replace(fullMatch, replacement);

        // Reset regex to start from beginning since we modified the string
        spanWithBreakRegex.lastIndex = 0;
      }
      return result;
    },
    getPositionInfo: function (subtitle, regionInfo, styleInfo, win) {
      let xPos,
        yPos,
        alignment = "center",
        extents,
        origins,
        displayAlign,
        totalLines;

      /* extent = size of element */
      /* origin = placement of element */
      extents = this.getExtents(subtitle, regionInfo);
      origins = this.getOrigins(subtitle, regionInfo);
      displayAlign = this.getDisplayAlign(subtitle, regionInfo);
      if (styleInfo && styleInfo["@_tts:textAlign"]) {
        alignment = flexbox.alignmentNormalize[styleInfo["@_tts:textAlign"]];
      } else if (regionInfo && regionInfo["@_tts:textAlign"]) {
        alignment = flexbox.alignmentNormalize[regionInfo["@_tts:textAlign"]];
      } else {
        alignment = flexbox.alignmentNormalize[this.getAlignment(subtitle, regionInfo)];
      }
      totalLines = (subtitle["#text"] || "").toString().split(/<br\/>|<br>|<br \/>/).length;
      let x = this.getXPos(origins, extents, win, alignment);
      let y = this.getYPos(origins, extents, displayAlign, win, totalLines);
      xPos = x.position;
      yPos = y.position;
      return {
        alignment: alignment,
        xPos: xPos,
        yPos: yPos
      };
    },
    getDisplayAlign: function (subtitle, regionInfo) {
      if (regionInfo && regionInfo["@_tts:displayAlign"]) {
        return regionInfo["@_tts:displayAlign"];
      } else if (subtitle["@_tts:displayAlign"]) {
        return subtitle["@_tts:displayAlign"];
      } else {
        return false;
      }
    },
    getExtents: function (subtitle, regionInfo) {
      if (regionInfo && regionInfo["@_tts:extent"]) {
        return {
          x: parseFloat(regionInfo["@_tts:extent"].split(" ")[0].replace("%", "")),
          y: parseFloat(regionInfo["@_tts:extent"].split(" ")[1].replace("%", ""))
        };
      } else if (subtitle["@_tts:extent"]) {
        return {
          x: parseFloat(subtitle["@_tts:extent"].split(" ")[0].replace("%", "")),
          y: parseFloat(subtitle["@_tts:extent"].split(" ")[1].replace("%", ""))
        };
      } else {
        return false;
      }
    },
    getOrigins: function (subtitle, regionInfo) {
      if (regionInfo && regionInfo["@_tts:origin"]) {
        return {
          x: parseFloat(regionInfo["@_tts:origin"].split(" ")[0].replace("%", "")),
          y: parseFloat(regionInfo["@_tts:origin"].split(" ")[1].replace("%", ""))
        };
      } else if (subtitle["@_tts:origin"]) {
        return {
          x: parseFloat(subtitle["@_tts:origin"].split(" ")[0].replace("%", "")),
          y: parseFloat(subtitle["@_tts:origin"].split(" ")[1].replace("%", ""))
        };
      } else {
        return false;
      }
    },
    getAlignment: function (subtitle, regionInfo) {
      if (subtitle["@_tts:textAlign"]) {
        return subtitle["@_tts:textAlign"];
      } else if (regionInfo && regionInfo["@_tts:textAlign"]) {
        return regionInfo["@_tts:textAlign"];
      } else {
        return "center";
      }
    },
    getXPos: function (origins, extents, win, alignment = "center") {
      //Origins: { x: 10, y: 50 }
      //Extents: { x: 40, y: 40 }
      //Window: { height: 15, width: 32, xPadding: 10, yPadding: 10 }
      //alignment: left | center | right

      /* 
          In order to convert origin and extent values from IMSC 1.1 caption files to Closed Caption Creator position values we need to look at the window width, window padding (xPadding), and the origin x value. Closed Caption Creator stores position values as start, center, end, or a character offset (1-width).
            We can also use the extent x value to help determine if the caption is aligned to the left, center, or right of the screen.
            Full safe-area width regions (origin at xPadding spanning to 100-xPadding) are geometrically both "centered boxes" and "start-origin boxes". In that case textAlign/alignment is authoritative: left→start, center→center, right→end.
            There are four main scenarios we need to consider:
          1. Full safe-area width + textAlign
              Use flexbox.alignmentMap to derive xPos from alignment.
          2. xPos = center
              If origin.x is equal to the center position calculation (100-extents.x)/2 then the caption is centered.
          3. xPos = start / end
              Adjusted origin ≈ 0 → start; origins.x + extents.x ≈ 100-xPadding → end.
          4. xPos = character offset (1-width)
              If none of the above conditions are met then we need to calculate the character offset based on the adjusted origin and window width. The formula for this is: (adjustedOriginX/100)*window.width. We then need to round this value and add 1 to convert it to a 1-based index.
      */

      let xInfo = {
        position: "center"
      };
      if (!origins || !extents || origins.x === undefined || extents.x === undefined || !win || !win.width) {
        return xInfo;
      }
      let xPadding = win.xPadding || 0;
      let width = Math.max(1, win.width);
      let extentX = Math.max(0, extents.x || 0);
      let adjustedOriginX = origins.x - xPadding;
      let centerExpected = (100 - extentX) / 2;
      let endExpected = 100 - xPadding;
      let tolerancePercent = Math.max(0.5, 100 / (width * 2)); // bias towards tighter tolerance on wider screens

      let isApproximately = (value, target) => Math.abs(value - target) <= tolerancePercent;
      let isFullSafeWidth = isApproximately(adjustedOriginX, 0) && isApproximately(origins.x + extentX, endExpected);

      // 1) Full safe-area width: textAlign decides horizontal placement
      if (isFullSafeWidth) {
        xInfo.position = flexbox.alignmentMap[alignment] || "center";
        return xInfo;
      }

      // 2) Center check for partial-width regions
      if (isApproximately(origins.x, centerExpected)) {
        xInfo.position = "center";
        return xInfo;
      }

      // 3) Start alignment
      if (isApproximately(adjustedOriginX, 0)) {
        xInfo.position = "start";
        return xInfo;
      }

      // 4) End alignment
      if (isApproximately(origins.x + extentX, endExpected)) {
        xInfo.position = "end";
        return xInfo;
      }

      // 5) Character offset fallback (1-based)
      let rawCharOffset = Math.round(adjustedOriginX / 100 * width);
      let charOffset = Math.min(width, Math.max(1, rawCharOffset + 1));
      if (charOffset <= 1 && adjustedOriginX <= tolerancePercent) {
        xInfo.position = "start";
      } else if (charOffset >= width && origins.x + extentX >= endExpected - tolerancePercent) {
        xInfo.position = "end";
      } else {
        xInfo.position = charOffset;
      }
      return xInfo;
    },
    getYPos: function (origins, extents, displayAlign, win, totalLines = 1) {
      //Origins: { x: 10, y: 50 }
      //Extents: { x: 40, y: 40 }
      //DisplayAlign: after 
      //Win: { height: 15, width: 32, xPadding: 10, yPadding: 10 }
      //totalLines: integer (default to 1)

      /* 
          Convert TTML origin/extent/displayAlign to CCC yPos (start | center | end | line 1..height).
            Lines map across the padded safe area:
              safeHeightPercent = 100 - 2*yPadding
              percentToLine(p) = ((p - yPadding) / safeHeightPercent) * height
            percentToLine returns a 0-based index when given the TOP of a line
          (the top of row n sits at the bottom of row n-1), and a 1-based row
          number when given the BOTTOM of a line.
          Bottom of line n is at: yPadding + (n / height) * safeHeightPercent
          e.g. height=15, yPadding=10 → line 12 bottom = 74%; line 15 top = 84.67% → percentToLine = 14 → row 15.
            displayAlign:
              after  → text at bottom of region: bottomLine - (totalLines - 1)
              before → text at top of region: percentToLine(origins.y) + 1 (convert 0-based top index to 1-based row)
              center → midpoint of region, adjusted by totalLines
      */

      let yInfo = {
        position: "end"
      };
      origins = origins || {
        x: 0,
        y: 0
      };
      if (!origins || origins.y === undefined || !extents || extents.y === undefined || !win || !win.height) {
        return yInfo;
      }
      let height = Math.max(1, win.height);
      let yPadding = Math.max(0, win.yPadding || 0);
      let safeHeightPercent = Math.max(1, 100 - 2 * yPadding);
      let lines = Math.max(1, totalLines);
      let percentToLine = percent => (percent - yPadding) / safeHeightPercent * height;
      let clampLine = line => Math.max(1, Math.min(height, Math.round(line)));
      let regionTop = origins.y;
      let regionBottom = origins.y + Math.max(0, extents.y || 0);
      let topLine = percentToLine(regionTop);
      let bottomLine = percentToLine(regionBottom);
      let midLine = (topLine + bottomLine) / 2;
      let screenCenter = (height + 1) / 2;
      let tolerance = Math.max(1, height * 0.1);
      let mapToFlexPosition = line => {
        let normalized = clampLine(line);
        let isStart = normalized <= 1;
        let isEnd = normalized >= height;
        let isCenter = Math.abs(normalized - screenCenter) <= tolerance;
        if (isStart) {
          return "start";
        }
        if (isEnd) {
          return "end";
        }
        if (isCenter) {
          return "center";
        }
        return normalized;
      };
      let align = (displayAlign || "before").toLowerCase();
      if (align === "after") {
        let startLine = bottomLine - (lines - 1);
        if (clampLine(bottomLine) >= height) {
          yInfo.position = lines === 1 ? "end" : mapToFlexPosition(startLine);
        } else {
          yInfo.position = mapToFlexPosition(startLine);
        }
        return yInfo;
      }
      if (align === "center") {
        let centeredStart = midLine - (lines - 1) / 2;
        let candidate = mapToFlexPosition(centeredStart);
        if (candidate === "start" || candidate === "center" || candidate === "end") {
          yInfo.position = candidate;
        } else if (Math.abs(midLine - screenCenter) <= tolerance) {
          yInfo.position = "center";
        } else {
          yInfo.position = candidate;
        }
        return yInfo;
      }

      // default to "before" - topLine is a 0-based index of the line top, so shift to a 1-based row
      let startLine = topLine + 1;
      if (lines > 1 && clampLine(startLine + (lines - 1)) >= height) {
        // Block's last line sits on the bottom row
        yInfo.position = "end";
      } else {
        yInfo.position = mapToFlexPosition(startLine);
      }
      return yInfo;
    },
    calcXOrigin(text, xPos, fontSize = 80, win) {
      let xOrigin = 0;
      let longestLine = getLongestLine(text);
      let xExtent = longestLine.length / win.width * (fontSize / 100) * 100; // Convert to percentage

      // Account for padding that will be applied
      let xPadding = win.xPadding || 0;
      if (typeof xPos === 'string') {
        // Handle flexbox values
        if (xPos === "start") {
          xOrigin = xPadding;
        } else if (xPos === "center") {
          xOrigin = findCenter(100, xExtent);
        } else if (xPos === "end") {
          xOrigin = 100 - xPadding - xExtent;
        }
      } else {
        // Handle integer character position (1-based)
        let charPosition = parseInt(xPos);
        if (charPosition >= 1 && charPosition <= win.width) {
          // Convert character position to percentage
          xOrigin = xPadding + (charPosition - 1) / win.width * 100;
        }
      }

      // Add back the padding that will be subtracted during decoding
      return parseFloat(xOrigin);
    },
    calcYOrigin(text, yPos, fontSize = 80, win) {
      let yOrigin = 0;
      let totalLines = eol.split(text).length;
      let lineHeight = 5.33; // Default line height percentage
      let yExtent = lineHeight * totalLines; // Convert to percentage

      // Account for padding that will be applied
      let yPadding = win.yPadding || 0;
      if (typeof yPos === 'string') {
        // Handle flexbox values
        if (yPos === "start") {
          yOrigin = yPadding;
        } else if (yPos === "center") {
          yOrigin = findCenter(100, yExtent);
        } else if (yPos === "end") {
          yOrigin = 100 - yPadding - yExtent;
        }
      } else {
        // Handle integer line position (1-based)
        let linePosition = parseInt(yPos);
        if (linePosition >= 1 && linePosition <= win.height) {
          // Convert line position to percentage
          // yOrigin = yPadding + (((linePosition - 1) / win.height) * 100);
          let paddingLines = win.height * (yPadding / 100);
          let totalHeightOfWindow = win.height + paddingLines * 2;
          yOrigin = (paddingLines + (linePosition - 1)) / totalHeightOfWindow * 100;
        } else {
          yOrigin = 100 - yPadding - yExtent; // Default to bottom if out of bounds
        }
      }

      // Add back the padding that will be subtracted during decoding
      return parseFloat(yOrigin);
    },
    calcExtents: function (text, fontSize = 80, lineHeight = 5.33) {
      let longestLine = getLongestLine(text);
      /* Calculate extents based on content size */
      let xExtent = longestLine.length / 32 * (fontSize / 100);
      let yExtent = lineHeight / 100 * eol.split(text).length;
      return `${(xExtent * 100).toFixed(2)}% ${(yExtent * 100).toFixed(2)}%`;
      /*e.g 37.5% 5.33% */
    },
    calcOrigin: function (text, xPos, yPos, fontSize = 80, win) {
      let xOrigin = this.calcXOrigin(text, xPos, fontSize, win);
      let yOrigin = this.calcYOrigin(text, yPos, fontSize, win);
      return `${xOrigin.toFixed(2)}% ${yOrigin.toFixed(2)}%`; /*e.g 37.5% 5.33% */
    },
    convertToTtml: function (html) {
      let getStyleProperty = (tag, propertyName) => {
        let styleMatches = tag.matchAll(/\sstyle\s*=\s*(['"])(.*?)\1/gi);
        let value = null;
        for (let styleMatch of styleMatches) {
          let styleRule = styleMatch[2].split(";").map(rule => rule.trim()).find(rule => {
            let separatorIndex = rule.indexOf(":");
            if (separatorIndex === -1) {
              return false;
            }
            return rule.slice(0, separatorIndex).trim().toLowerCase() === propertyName;
          });
          if (styleRule) {
            value = styleRule.slice(styleRule.indexOf(":") + 1).trim();
          }
        }
        return value;
      };
      let convertStyleTag = (tag, styleAttributes) => {
        let color = getStyleProperty(tag, "color");
        let backgroundColor = getStyleProperty(tag, "background-color");
        let attributes = [...styleAttributes];
        if (color) {
          color = toWebColor(color);
          attributes.push(`tts:color='${color}'`);
        }
        if (backgroundColor) {
          attributes.push(`tts:backgroundColor='${toWebColor(backgroundColor)}'`);
        }
        return `<span ${attributes.join(" ")}>`;
      };

      // console.log("---------------");
      // console.log("BEFORE:", html);
      html = html.replace(/(<strong\b[^>]*><em\b[^>]*><u\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontWeight='bold'", "tts:fontStyle='italic'", "tts:textDecoration='underline'"])).replace(/(<\/u><\/em><\/strong>)+/gmi, "</span>").replace(/(<strong\b[^>]*><u\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontWeight='bold'", "tts:textDecoration='underline'"])).replace(/(<\/u><\/strong>)+/gmi, "</span>").replace(/(<strong\b[^>]*><em\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontWeight='bold'", "tts:fontStyle='italic'"])).replace(/(<\/em><\/strong>)+/gmi, "</span>").replace(/(<em\b[^>]*><strong\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontWeight='bold'", "tts:fontStyle='italic'"])).replace(/(<\/strong><\/em>)+/gmi, "</span>").replace(/(<strong\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontWeight='bold'"])).replace(/(<\/strong>)+/gmi, "</span>").replace(/(<em\b[^>]*><u\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontStyle='italic'", "tts:textDecoration='underline'"])).replace(/(<\/u><\/em>)+/gmi, "</span>").replace(/(<em\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:fontStyle='italic'"])).replace(/(<\/em>)+/gmi, "</span>").replace(/(<u\b[^>]*>)+/gmi, tag => convertStyleTag(tag, ["tts:textDecoration='underline'"])).replace(/(<\/u>)+/gmi, "</span>").replace(/<span\s+style=['"]color:\s*([^;'"]+)[;]?['"]/gi, (match, color) => `<span tts:color='${toWebColor(color)}'`).replace(/<span\s+style=['"]background-color:\s*([^;'"]+)[;]?['"]/gi, (match, color) => `<span tts:backgroundColor='${toWebColor(color)}'`);
      html = combineSpanTags(html);
      html = cleanExtraSpanTags(html);

      // console.log("AFTER:", html);
      html = html.replace(/(<\/span>)+/g, "</span>").replace(/(?:\r\n|\r|\n)/g, "<br/>");
      return escapeTextContent(html);
    }
  }
};
export default ttmlGeneral;